// `Profil` settings section (US-182, US-183, US-188, US-203).
//
// Composes the Avatar uploader, name + timezone form, language picker,
// and the gap-design email-change flow. Save is "save all on submit"
// (Enregistrer) so a single network round-trip persists the editable
// preference fields. The avatar + email rows have their own pipelines
// (presigned URL / Better Auth) and don't pass through this form.

import { useEffect } from 'react';

import { useMe, useUpdateProfile } from '@barel/api-client/react';
import { useLocale, useSetLocale, useT } from '@barel/i18n';
import type { Locale } from '@barel/i18n';
import { Banner, Button, Field, Input, Segmented, Select, toast } from '@barel/ui';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { AvatarUploader } from '../components/AvatarUploader';
import { EmailChangeFlow } from '../components/EmailChangeFlow';
import { CURATED_TIMEZONES, detectBrowserTimezone } from '../lib/timezones';

const SCHEMA = z.object({
  name: z.string().trim().min(1).max(120),
  timezone: z.string().trim().min(1).max(64),
});

type FormValues = z.infer<typeof SCHEMA>;

export function Profil() {
  const t = useT('settings');
  const me = useMe();
  const update = useUpdateProfile();
  const locale = useLocale();
  const setLocale = useSetLocale();
  const user = me.data?.user;

  const form = useForm<FormValues>({
    resolver: zodResolver(SCHEMA),
    mode: 'onBlur',
    defaultValues: {
      name: '',
      timezone: detectBrowserTimezone(),
    },
  });

  // Hydrate the form once `me` lands.
  useEffect(() => {
    if (!user) return;
    form.reset({
      name: typeof user.name === 'string' ? user.name : '',
      timezone:
        typeof user.timezone === 'string' && user.timezone
          ? user.timezone
          : detectBrowserTimezone(),
    });
  }, [user, form]);

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    try {
      await update.mutateAsync({
        name: values.name,
        timezone: values.timezone,
      });
      toast.success({ title: t('profile.saved') });
    } catch {
      toast.error({ title: t('profile.saveError') });
    }
  };

  const handleLocaleChange = async (next: Locale): Promise<void> => {
    setLocale(next);
    try {
      await update.mutateAsync({ uiLang: next });
    } catch {
      toast.error({ title: t('profile.saveError') });
    }
  };

  return (
    <section
      data-section="profile"
      aria-labelledby="settings-profile-title"
      className="flex flex-col gap-6"
    >
      <header className="flex flex-col gap-1 border-b border-default pb-4">
        <h1 id="settings-profile-title" className="text-lg font-semibold text-primary">
          {t('profile.title')}
        </h1>
        <p className="text-xs text-tertiary">{t('profile.subtitle')}</p>
      </header>

      <AvatarUploader user={user} />

      <EmailChangeFlow currentEmail={user?.email ?? ''} />

      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          void form.handleSubmit(onSubmit)(event);
        }}
        data-testid="settings-profile-form"
        noValidate
      >
        <Field invalid={!!form.formState.errors.name} required>
          <Field.Label>{t('profile.nameLabel')}</Field.Label>
          <Field.Description>{t('profile.nameHint')}</Field.Description>
          <Field.Control>
            <Input
              {...form.register('name')}
              type="text"
              autoComplete="name"
              placeholder={t('profile.namePlaceholder')}
              data-testid="settings-profile-name"
            />
          </Field.Control>
        </Field>

        <Field>
          <Field.Label>{t('profile.languageLabel')}</Field.Label>
          <Field.Description>{t('profile.languageHint')}</Field.Description>
          <Field.Control asChild>
            <div role="group" aria-label={t('profile.languageLabel')}>
              <Segmented<Locale>
                value={locale}
                onValueChange={(next) => {
                  void handleLocaleChange(next);
                }}
                options={[
                  { value: 'fr', label: t('profile.languageFr') },
                  { value: 'en', label: t('profile.languageEn') },
                ]}
                aria-label={t('profile.languageLabel')}
                size="sm"
                data-testid="settings-profile-locale"
              />
            </div>
          </Field.Control>
        </Field>

        <Field invalid={!!form.formState.errors.timezone} required>
          <Field.Label>{t('profile.timezoneLabel')}</Field.Label>
          <Field.Description>{t('profile.timezoneHint')}</Field.Description>
          <Field.Control asChild>
            <Select
              value={form.watch('timezone')}
              onValueChange={(next: string) => {
                form.setValue('timezone', next, { shouldDirty: true, shouldValidate: true });
              }}
            >
              <Select.Trigger
                aria-label={t('profile.timezoneLabel')}
                data-testid="settings-profile-timezone"
              >
                <Select.Value />
              </Select.Trigger>
              <Select.Portal>
                <Select.Content>
                  {CURATED_TIMEZONES.map((tz) => (
                    <Select.Item key={tz.id} value={tz.id}>
                      {tz.label}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Portal>
            </Select>
          </Field.Control>
        </Field>

        {update.isError ? <Banner tone="error">{t('profile.saveError')}</Banner> : null}

        <div className="flex items-center gap-2">
          <Button
            type="submit"
            variant="primary"
            size="md"
            loading={form.formState.isSubmitting || update.isPending}
            disabled={!form.formState.isDirty}
            data-testid="settings-profile-save"
          >
            {t('profile.save')}
          </Button>
        </div>
      </form>
    </section>
  );
}
